import {
  type DemoAppointment,
  type DemoAppointmentStatus,
  intakeSectionsToReview,
  type SchedulingIntakeReviewListItem,
  type SchedulingIntakeReviewSummary,
  type SchedulingIntakeSection,
  type SchedulingMessageAttention,
  type SchedulingPatientMatchState,
  type SchedulingPatientMatchSummary,
  schedulingMessageNeedsPerson,
  schedulingPatientMatchUnresolved,
} from "@emr/contracts";
import { patientMessageProblemLabel } from "./patientMessageCopy";

/*
 * The one rule for what a booking's row asks of staff (lead decision S2). Today's Expected and
 * board rows, the Rooms view Lobby, and Schedule's cards, agenda rows and side panel all read it,
 * so a row never shows two competing pills or actions. Highest precedence first:
 *
 * 1. review_booking: the booking needs its chart matched and has no questionnaire to reconcile
 *    it from. Charts matched when a returning patient booked online that staff have not resolved
 *    (PA-10: the booking's `patientMatch` is likely, several or card_other_dob), or a booking
 *    still without a chart before arrival. A booking with a match state shows that state as its
 *    pill ("Likely match", "Several possible charts", "No chart found", "Not a match", "Card on a
 *    chart with another birth date"); any other shows "Chart to link". A submitted
 *    questionnaire carries its own chart reconciler (Review intake, which lists the same charts),
 *    so that booking always falls to rule 2, whatever its match state: Schedule, Today and the
 *    review list then show one pill, one action and one count for it.
 * 2. review_intake: a submitted questionnaire has a section the signed-in member reviews still
 *    awaiting review. The server sends only the sections the member may review, so the pill is
 *    role-aware without knowing the role: physicians and nurses see "Health answers to review"
 *    until the clinical section is reviewed; everyone else sees the administrative state.
 * 3. show_code: a walk-in intake link was issued and the patient has not answered yet (PA-5
 *    passes `walkInLinkWaiting`).
 * 4. review_messages: a text or email to a durable booking before arrival needs a person (PA-7:
 *    the booking's `messageAttention` is a failure, "Text not delivered" / "Email not
 *    delivered", or a contact the patient agreed to that cannot be used, "Check phone number" /
 *    "Check email address"). Nobody has marked it done; "Review messages" opens the booking's
 *    texts and emails, where staff resend, correct the contact or mark it done.
 *
 * Otherwise there is no action. A questionnaire that was reviewed, or whose review closed
 * unreviewed, still shows a quiet pill that opens it read-only. Without one, a number that
 * replied STOP shows a quiet "Texts stopped" pill that opens the booking's messages. A message
 * that may not have been sent (uncertain) shows no pill: it settles by itself within 30 minutes,
 * as delivered or as a failure (rule 4), and the messages show it meanwhile. Sent and delivered
 * messages never show on the row. Pills sit on the row; nothing is inserted above the list.
 */

export type AppointmentRowActionKind =
  | "review_booking"
  | "review_intake"
  | "show_code"
  | "review_messages";

export interface AppointmentRowAction {
  readonly kind: AppointmentRowActionKind;
  readonly label: string;
}

export type AppointmentRowPillTone = "attention" | "done" | "muted";

export interface AppointmentRowPill {
  /**
   * What the pill opens: the booking, the questionnaire review, the walk-in code, or the
   * booking's texts and emails (PA-7).
   */
  readonly kind: "booking" | "intake" | "code" | "message";
  readonly tone: AppointmentRowPillTone;
  readonly label: string;
  /** For compact calendar cards. */
  readonly shortLabel: string;
}

export interface AppointmentRowState {
  readonly action: AppointmentRowAction | null;
  readonly pill: AppointmentRowPill | null;
  /** The sections the viewer still reviews (rule 2), clinical first. */
  readonly sectionsToReview: readonly SchedulingIntakeSection[];
}

/** What a later ticket learns about a row that the schedule read does not carry. */
export interface AppointmentRowSignals {
  /** PA-5: a walk-in's intake link is out and the patient has not answered yet. */
  readonly walkInLinkWaiting?: boolean | undefined;
}

/** The facts the rule reads, from a schedule appointment or a review-list item. */
export interface AppointmentRowFacts {
  /** A durable booking (version-tracked); the demo scheduler has no chart link or review. */
  readonly durable: boolean;
  readonly status: DemoAppointmentStatus;
  readonly chartLinked: boolean;
  /** The patient still has to answer a questionnaire before check-in. */
  readonly intakePending: boolean;
  readonly intakeReview?: SchedulingIntakeReviewSummary | undefined;
  /**
   * PA-10: how the charts matched when a returning patient booked online stand (the schedule
   * read's state only, never a chart). Only on such a booking without a chart, before arrival.
   */
  readonly patientMatch?: SchedulingPatientMatchSummary | undefined;
  /**
   * PA-7: the booking's text or email that staff should know about (the schedule read's
   * DemoAppointment.messageAttention). Only on a durable booking before arrival.
   */
  readonly messageAttention?: SchedulingMessageAttention | undefined;
}

const PRE_ARRIVAL: ReadonlySet<DemoAppointmentStatus> = new Set(["booked", "confirmed"]);

export function appointmentRowFacts(appointment: DemoAppointment): AppointmentRowFacts {
  return {
    durable: appointment.version !== undefined,
    status: appointment.status,
    chartLinked: appointment.patient.patientId !== undefined,
    intakePending: appointment.intakeStatus === "required" || appointment.intakeStatus === "sent",
    intakeReview: appointment.intakeReview,
    patientMatch: appointment.patientMatch,
    messageAttention: appointment.messageAttention,
  };
}

export function intakeListRowFacts(item: SchedulingIntakeReviewListItem): AppointmentRowFacts {
  return {
    durable: true,
    status: item.status,
    chartLinked: item.chartLinked,
    intakePending: false,
    intakeReview: item.intakeReview,
  };
}

/**
 * The words for a booking's chart-match state (PA-10), on pills and in Schedule's side panel. The
 * card wording is for staff only: it says a health card the patient gave sits on another chart.
 */
export const PATIENT_MATCH_LABELS: Readonly<
  Record<SchedulingPatientMatchState, { readonly label: string; readonly shortLabel: string }>
> = {
  likely: { label: "Likely match", shortLabel: "Likely match" },
  several: { label: "Several possible charts", shortLabel: "Several charts" },
  none: { label: "No chart found", shortLabel: "No chart" },
  mismatch: { label: "Not a match", shortLabel: "Not a match" },
  card_other_dob: {
    label: "Card on a chart with another birth date",
    shortLabel: "Check birth date",
  },
};

/** What the desk does next for each match state, before check-in (Schedule's side panel). */
export const PATIENT_MATCH_NEXT_STEPS: Readonly<Record<SchedulingPatientMatchState, string>> = {
  likely: "Confirm the chart in Review booking first.",
  several: "Choose the chart in Review booking first.",
  card_other_dob: "Card is on a chart with another birth date. Check it in Review booking.",
  none: "Find the chart or ask for details in Review booking.",
  mismatch: "Find the chart or ask for details in Review booking.",
};

function bookingPill(match: SchedulingPatientMatchSummary | undefined): AppointmentRowPill {
  if (match === undefined) {
    return { kind: "booking", tone: "attention", label: "Chart to link", shortLabel: "Link chart" };
  }
  return { kind: "booking", tone: "attention", ...PATIENT_MATCH_LABELS[match.state] };
}

/** The questionnaire's own pill when no rule asks anything of the row. */
function quietIntakePill(review: SchedulingIntakeReviewSummary): AppointmentRowPill {
  const states = [review.administrative.state, review.clinical?.state].filter(
    (state) => state !== undefined,
  );
  return states.every((state) => state === "reviewed")
    ? { kind: "intake", tone: "done", label: "Intake reviewed", shortLabel: "Reviewed" }
    : { kind: "intake", tone: "muted", label: "Intake not reviewed", shortLabel: "Not reviewed" };
}

export function appointmentRowState(
  facts: AppointmentRowFacts,
  signals: AppointmentRowSignals = {},
): AppointmentRowState {
  const sectionsToReview = intakeSectionsToReview(facts.intakeReview);
  const needsChartMatch =
    facts.intakeReview === undefined &&
    (schedulingPatientMatchUnresolved(facts.patientMatch) ||
      (facts.durable &&
        !facts.chartLinked &&
        PRE_ARRIVAL.has(facts.status) &&
        !facts.intakePending));
  if (needsChartMatch) {
    return {
      action: { kind: "review_booking", label: "Review booking" },
      pill: bookingPill(facts.patientMatch),
      sectionsToReview,
    };
  }
  if (sectionsToReview.length > 0) {
    const health = sectionsToReview.includes("clinical");
    return {
      action: { kind: "review_intake", label: "Review intake" },
      pill: health
        ? {
            kind: "intake",
            tone: "attention",
            label: "Health answers to review",
            shortLabel: "Health to review",
          }
        : {
            kind: "intake",
            tone: "attention",
            label: "Intake to review",
            shortLabel: "Review intake",
          },
      sectionsToReview,
    };
  }
  if (signals.walkInLinkWaiting === true) {
    return {
      action: { kind: "show_code", label: "Show code" },
      pill: {
        kind: "code",
        tone: "attention",
        label: "Waiting for answers",
        shortLabel: "Waiting",
      },
      sectionsToReview,
    };
  }
  const message =
    facts.durable && PRE_ARRIVAL.has(facts.status) ? facts.messageAttention : undefined;
  if (message !== undefined && schedulingMessageNeedsPerson(message)) {
    return {
      action: { kind: "review_messages", label: "Review messages" },
      pill: {
        kind: "message",
        tone: "attention",
        ...patientMessageProblemLabel(
          message.status === "withheld" ? "withheld" : "failed",
          message.channel,
        ),
      },
      sectionsToReview,
    };
  }
  return {
    action: null,
    pill:
      facts.intakeReview !== undefined
        ? quietIntakePill(facts.intakeReview)
        : message?.status === "stopped"
          ? {
              kind: "message",
              tone: "muted",
              ...patientMessageProblemLabel("stopped", message.channel),
            }
          : null,
    sectionsToReview,
  };
}

/** The pill's words for a screen reader: what it is, then what opening it does. */
export function appointmentRowPillDescription(pill: AppointmentRowPill, name: string): string {
  switch (pill.kind) {
    case "intake":
      return pill.tone === "attention"
        ? `Review intake for ${name}: ${pill.label}`
        : `Open intake for ${name}: ${pill.label}`;
    case "booking":
      return `Review booking for ${name}: ${pill.label}`;
    case "code":
      return `Show the intake code for ${name}: ${pill.label}`;
    case "message":
      return pill.tone === "attention"
        ? `Review texts and emails for ${name}: ${pill.label}`
        : `Open texts and emails for ${name}: ${pill.label}`;
  }
}
